const MAX_CONTOURS = 500;
const MAX_POINTS_PER_CONTOUR = 1200;
const COORDINATE_PRECISION = 6;

export function normalizeManualCutContours(input, expectedCount = 0) {
  if (!Array.isArray(input) || !input.length || input.length > MAX_CONTOURS) return null;
  const contours = [];
  for (const source of input) {
    if (!Array.isArray(source) || source.length < 3 || source.length > MAX_POINTS_PER_CONTOUR) return null;
    const points = [];
    for (const point of source) {
      const x = Number(point?.x);
      const y = Number(point?.y);
      if (!Number.isFinite(x) || !Number.isFinite(y) || x < 0 || x > 1 || y < 0 || y > 1) return null;
      points.push({ x: rounded(x), y: rounded(y) });
    }
    contours.push(removeAdjacentDuplicates(points));
  }
  if (expectedCount > 0 && contours.length !== Number(expectedCount)) return null;
  return contours.every((points) => points.length >= 3) ? contours : null;
}

export function resolveEffectiveCutContours(autoContours, manualContours, expectedCount = 0) {
  const normalizedAuto = normalizeManualCutContours(autoContours, expectedCount) || [];
  const normalizedManual = normalizeManualCutContours(manualContours, expectedCount);
  return {
    contours: normalizedManual || normalizedAuto,
    autoContours: normalizedAuto,
    manual: Boolean(normalizedManual),
  };
}

export function contourBounds(points = []) {
  if (!points.length) return { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0, centerX: 0.5, centerY: 0.5 };
  const xs = points.map((point) => Number(point.x));
  const ys = points.map((point) => Number(point.y));
  const left = Math.min(...xs);
  const top = Math.min(...ys);
  const right = Math.max(...xs);
  const bottom = Math.max(...ys);
  return {
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
    centerX: (left + right) / 2,
    centerY: (top + bottom) / 2,
  };
}

export function transformContour(points, transform = {}) {
  const bounds = contourBounds(points);
  const scaleX = Number.isFinite(Number(transform.scaleX)) ? Number(transform.scaleX) : 1;
  const scaleY = Number.isFinite(Number(transform.scaleY)) ? Number(transform.scaleY) : 1;
  const translateX = Number(transform.translateX) || 0;
  const translateY = Number(transform.translateY) || 0;
  return (points || []).map((point) => ({
    x: rounded(clamp(bounds.centerX + (point.x - bounds.centerX) * scaleX + translateX)),
    y: rounded(clamp(bounds.centerY + (point.y - bounds.centerY) * scaleY + translateY)),
  }));
}

export function replaceContourPoint(points, pointIndex, point) {
  if (!Array.isArray(points) || pointIndex < 0 || pointIndex >= points.length) return points;
  return points.map((current, index) => index === pointIndex
    ? { x: rounded(clamp(Number(point.x))), y: rounded(clamp(Number(point.y))) }
    : current);
}

export function insertContourPoint(points, pointIndex) {
  if (!Array.isArray(points) || points.length < 2) return points;
  const index = Math.max(0, Math.min(points.length - 1, Number(pointIndex) || 0));
  const nextIndex = (index + 1) % points.length;
  const midpoint = {
    x: rounded((points[index].x + points[nextIndex].x) / 2),
    y: rounded((points[index].y + points[nextIndex].y) / 2),
  };
  return [...points.slice(0, index + 1), midpoint, ...points.slice(index + 1)];
}

export function removeContourPoint(points, pointIndex) {
  if (!Array.isArray(points) || points.length <= 3) return points;
  return points.filter((_, index) => index !== pointIndex);
}

export function validateEditableCutContours(contours, expectedCount = 0) {
  const issues = [];
  if (!Array.isArray(contours) || !contours.length) issues.push("Ingen kuttlinjer er definert.");
  if (expectedCount > 0 && contours?.length !== Number(expectedCount)) issues.push(`Forventet ${expectedCount} kuttlinjer, men fant ${contours?.length || 0}.`);
  let pointCount = 0;
  let edgeRiskCount = 0;
  let selfIntersectionCount = 0;
  for (let contourIndex = 0; contourIndex < (contours || []).length; contourIndex += 1) {
    const points = contours[contourIndex] || [];
    pointCount += points.length;
    if (points.length < 3) issues.push(`Kuttlinje ${contourIndex + 1} har for få punkter.`);
    if (points.some((point) => !Number.isFinite(Number(point.x)) || !Number.isFinite(Number(point.y)) || point.x < 0 || point.x > 1 || point.y < 0 || point.y > 1)) {
      issues.push(`Kuttlinje ${contourIndex + 1} går utenfor produksjonsflaten.`);
    }
    const bounds = contourBounds(points);
    if (Math.min(bounds.left, bounds.top, 1 - bounds.right, 1 - bounds.bottom) < 0.001) edgeRiskCount += 1;
    if (hasSelfIntersection(points)) selfIntersectionCount += 1;
  }
  if (selfIntersectionCount) issues.push(`${selfIntersectionCount} kuttlinje${selfIntersectionCount === 1 ? "" : "r"} krysser seg selv.`);
  return {
    valid: issues.length === 0,
    issues,
    contourCount: contours?.length || 0,
    pointCount,
    edgeRiskCount,
    selfIntersectionCount,
  };
}

function hasSelfIntersection(points) {
  if (!Array.isArray(points) || points.length < 4) return false;
  for (let first = 0; first < points.length; first += 1) {
    const firstNext = (first + 1) % points.length;
    for (let second = first + 2; second < points.length; second += 1) {
      const secondNext = (second + 1) % points.length;
      if (first === secondNext || firstNext === second) continue;
      if (segmentsIntersect(points[first], points[firstNext], points[second], points[secondNext])) return true;
    }
  }
  return false;
}

function segmentsIntersect(a, b, c, d) {
  const orientation = (p, q, r) => Math.sign((q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y));
  return orientation(a, b, c) !== orientation(a, b, d) && orientation(c, d, a) !== orientation(c, d, b);
}

function removeAdjacentDuplicates(points) {
  return points.filter((point, index) => {
    const previous = points[(index - 1 + points.length) % points.length];
    return point.x !== previous.x || point.y !== previous.y;
  });
}

function rounded(value) {
  return Number(Number(value).toFixed(COORDINATE_PRECISION));
}

function clamp(value) {
  return Math.max(0, Math.min(1, Number(value) || 0));
}
